import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../config';
import qs from 'qs';
import { accessToken } from '../../configuration/token-handling/accessToken';
import {
    type ApiTransportOrdersForShipmentsResponse,
    transportOrdersForShipmentsResponseSchema,
} from './transportExecution.transportOrder.schema';
import type { Shipment, Status } from '../../domain/model/shipment/Shipment.types';
import type { Packaging } from '../../domain/model/packaging/Packaging.types';
import type { AvailableActionsForShipment } from '../../domain/model/available-actions/AvailableActions.types';
import type {
    DeviationDocuments,
    ReportDeviations,
    ReportDeviationsResponse,
    ReportedBy,
} from '../../domain/model/deviation/Deviation.types';
import type { TransferType } from '../../domain/model/common.types';
import type { TransportOrder } from '../../domain/model/transport/Transport.types';
import type { ShipmentHistoryEntry } from '../../domain/model/shipment/ShipmentHistory.types';
import { type ApiGetShipmentsResponse, getShipmentsResponseSchema } from './transportExecution.shipments.schema';
import {
    type ApiGetPackagingsQuery,
    type ApiGetPackagingsResponse,
    getPackagingsQuerySchema,
    getPackagingsResponseSchema,
} from './transportExecution.packaging.schema';
import {
    type ApiGetAvailableActionsQuery,
    type ApiGetAvailableActionsResponse,
    getAvailableActionsRequestSchema,
    getAvailableActionsResponseSchema,
} from './transportExecution.availableActions.schema';
import {
    type ApiConfirmShipmentTransferRequest,
    confirmShipmentTransferRequestSchema,
    confirmShipmentTransferResponseSchema,
} from './transportExecution.confirmShipmentTransfer.schema';
import {
    type ApiReportDeviationsRequest,
    deviationDocumentsSchema,
    reportDeviationsRequestSchema,
    reportDeviationsResponseSchema,
} from './transportExecution.reportDeviations.schema';
import {
    type ApiGetShipmentsHistoryResponse,
    getShipmentHistoryResponseSchema,
    type ApiShipmentHistoryDocumentDownloadResponse,
    shipmentHistoryDocumentDownloadResponseSchema,
} from './transportExecution.shipmentHistory.schema';
import { type ApiGetMeResponse, getMeResponseSchema } from './transportExecution.me.schema';
import { toAvailableActionsForShipment } from './transportExecution.availableActions.mapper';
import { toUserContext } from './transportExecution.me.mapper';
import { toPackaging } from './transportExecution.packaging.mapper';
import {
    toApiDeviationDocuments,
    toApiReportDeviations,
    toReportDeviationsResponse,
} from './transportExecution.reportDeviations.mapper';
import { toShipmentHistoryEntry } from './transportExecution.shipmentHistory.mapper';
import { toApiShipmentDateTypeQuery, toApiStatus, toShipment } from './transportExecution.shipments.mapper';
import { toTransportOrdersForShipment } from './transportExecution.transportOrder.mapper';
import type { ShipmentDateType } from '../../features/shipments-overview/redux/shipmentsOverview.slice';
import type { UserContext } from '../../domain/model/user-context/UserContext.types';
import type { PackagingMasterDataSearchResult } from '../../domain/model/packaging/PackagingMasterData.types';
import {
    type ApiPackagingMasterDataSearchResponse,
    packagingMasterDataSearchResponseSchema,
} from './transportExecution.packagingMasterData.schema';
import { toPackagingMasterDataSearchResults } from './transportExecution.packagingMasterData.mapper';

const url = config.transportExecutionApi.baseUrl;

const GET_SHIPMENTS_API_TAG = 'GET_SHIPMENTS';
const GET_SHIPMENTS_HISTORY_API_TAG = 'GET_SHIPMENTS_HISTORY';
const GET_PACKAGINGS_API_TAG = 'GET_PACKAGINGS';
const GET_AVAILABLE_ACTIONS_API_TAG = 'GET_AVAILABLE_ACTIONS';
const GET_TRANSPORT_ORDERS_API_TAG = 'GET_TRANSPORT_ORDERS';

interface AccessScopeParams {
    readonly roles?: string[];
    readonly dunsNumbers?: string[];
}

export type GetShipmentsQuery = {
    readonly status?: Status[];
    readonly dateType: ShipmentDateType;
    readonly dateFrom: string;
    readonly dateTo: string;
} & AccessScopeParams;

export type GetShipmentsResult = {
    readonly items: Shipment[];
    readonly hasMore: boolean;
};

export type ShipmentIdScopedQuery = {
    shipmentId: string;
} & AccessScopeParams;

export type ShipmentHistoryDocumentDownloadMutation = ShipmentIdScopedQuery & {
    documentId: string;
};

export type ConfirmShipmentTransferMutation = {
    readonly shipmentId: string;
    readonly transportLegId: string;
    readonly transferType: TransferType;
    readonly transferredAt: string;
    readonly reportedBy: ReportedBy;
};

export type ReportDeviationsMutation = {
    shipmentId: string;
    deviations: ReportDeviations;
};

export type DeviationDocumentsMutation = {
    shipmentId: string;
    transferConfirmationId: string;
    document: DeviationDocuments;
};

export type SearchPackagingMasterDataQuery = {
    readonly shipmentId: string;
    readonly searchTerm: string;
    readonly type?: string;
    readonly limit: number;
} & AccessScopeParams;

type ShipmentIdsScopedQuery = {
    shipmentIds: string[];
} & AccessScopeParams;

export const transportExecutionApi = createApi({
    reducerPath: 'transportExecutionApi',
    tagTypes: [
        GET_SHIPMENTS_API_TAG,
        GET_SHIPMENTS_HISTORY_API_TAG,
        GET_PACKAGINGS_API_TAG,
        GET_AVAILABLE_ACTIONS_API_TAG,
        GET_TRANSPORT_ORDERS_API_TAG,
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: url,
        prepareHeaders: headers => {
            headers.set('authorization', `Bearer ${accessToken.getAccessToken()}`);
            headers.set('accept', 'application/json');
            return headers;
        },
        paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' }),
    }),
    endpoints: builder => ({
        getMe: builder.query<UserContext, void>({
            query: () => ({
                url: '/transport-execution/me',
            }),
            transformResponse: (response: unknown) => {
                const parsed: ApiGetMeResponse = getMeResponseSchema.parse(response);
                return toUserContext(parsed);
            },
        }),
        getShipments: builder.query<GetShipmentsResult, GetShipmentsQuery>({
            query: (params: GetShipmentsQuery) => ({
                url: '/transport-execution/shipments',
                params: {
                    date_type: toApiShipmentDateTypeQuery(params.dateType),
                    date_from: params.dateFrom,
                    date_to: params.dateTo,
                    status: params.status?.map(toApiStatus),
                    role: params.roles,
                    duns: params.dunsNumbers,
                },
            }),
            transformResponse: (response: unknown) => {
                const parsed: ApiGetShipmentsResponse = getShipmentsResponseSchema.parse(response);
                return {
                    items: parsed.items.map(toShipment),
                    hasMore: parsed.has_more,
                };
            },
            providesTags: [{ type: GET_SHIPMENTS_API_TAG }],
        }),
        getShipmentHistory: builder.query<ShipmentHistoryEntry[], ShipmentIdScopedQuery>({
            query: params => ({
                url: `/transport-execution/shipments/${params.shipmentId}/history`,
                params: {
                    role: params.roles,
                    duns: params.dunsNumbers,
                },
            }),
            transformResponse: (response: unknown) => {
                const parsed: ApiGetShipmentsHistoryResponse = getShipmentHistoryResponseSchema.parse(response);
                return parsed.items.map(toShipmentHistoryEntry);
            },
            providesTags: [{ type: GET_SHIPMENTS_HISTORY_API_TAG }],
        }),
        downloadShipmentHistoryDocument: builder.mutation<string, ShipmentHistoryDocumentDownloadMutation>({
            query: params => ({
                url: `/transport-execution/shipments/${params.shipmentId}/history/documents/${params.documentId}/download`,
                method: 'GET',
                params: {
                    role: params.roles,
                    duns: params.dunsNumbers,
                },
            }),
            transformResponse: (response: unknown) => {
                const parsed: ApiShipmentHistoryDocumentDownloadResponse =
                    shipmentHistoryDocumentDownloadResponseSchema.parse(response);
                return parsed.url;
            },
        }),
        getPackagings: builder.query<Packaging[], ShipmentIdsScopedQuery>({
            query: params => {
                const requestBody: ApiGetPackagingsQuery = { shipment_ids: params.shipmentIds };
                getPackagingsQuerySchema.parse(requestBody);

                return {
                    url: '/transport-execution/packagings',
                    method: 'POST',
                    body: requestBody,
                    params: {
                        role: params.roles,
                        duns: params.dunsNumbers,
                    },
                };
            },
            transformResponse: (response: unknown, _meta: unknown, arg: ShipmentIdsScopedQuery) => {
                const parsed: ApiGetPackagingsResponse = getPackagingsResponseSchema.parse(response);

                return arg.shipmentIds.map(id => {
                    const found = parsed.items.find(i => i.shipment_id === id);
                    if (found) {
                        return toPackaging(found);
                    } else {
                        return { shipmentId: id };
                    }
                });
            },
            providesTags: [{ type: GET_PACKAGINGS_API_TAG }],
        }),
        searchPackagingMasterData: builder.query<PackagingMasterDataSearchResult[], SearchPackagingMasterDataQuery>({
            query: params => {
                return {
                    url: '/transport-execution/packaging-master-data',
                    params: {
                        shipment_id: params.shipmentId,
                        q: params.searchTerm,
                        type: params.type,
                        limit: params.limit,
                        role: params.roles,
                        duns: params.dunsNumbers,
                    },
                };
            },
            transformResponse: (response: unknown) => {
                const parsed: ApiPackagingMasterDataSearchResponse =
                    packagingMasterDataSearchResponseSchema.parse(response);
                return toPackagingMasterDataSearchResults(parsed);
            },
        }),
        confirmShipmentTransfer: builder.mutation<void, ConfirmShipmentTransferMutation>({
            query: (params: ConfirmShipmentTransferMutation) => {
                const requestBody: ApiConfirmShipmentTransferRequest = {
                    transport_leg_id: params.transportLegId,
                    reported_by: {
                        duns: params.reportedBy.duns,
                        role: params.reportedBy.role,
                    },
                    transferred_at: params.transferredAt,
                    transfer_type: params.transferType,
                };
                confirmShipmentTransferRequestSchema.parse(requestBody);

                return {
                    url: `/transport-execution/shipments/${params.shipmentId}/confirm-transfer`,
                    method: 'POST',
                    body: requestBody,
                };
            },
            transformResponse: (response: unknown) => {
                confirmShipmentTransferResponseSchema.parse(response);
            },
            invalidatesTags: [
                { type: GET_SHIPMENTS_API_TAG },
                { type: GET_AVAILABLE_ACTIONS_API_TAG },
                { type: GET_TRANSPORT_ORDERS_API_TAG },
                { type: GET_SHIPMENTS_HISTORY_API_TAG },
            ],
        }),
        getAvailableActions: builder.query<AvailableActionsForShipment[], ShipmentIdsScopedQuery>({
            query: params => {
                const requestBody: ApiGetAvailableActionsQuery = { shipment_ids: params.shipmentIds };
                getAvailableActionsRequestSchema.parse(requestBody);

                return {
                    url: '/transport-execution/available-actions',
                    method: 'POST',
                    body: requestBody,
                    params: {
                        role: params.roles,
                        duns: params.dunsNumbers,
                    },
                };
            },
            transformResponse: (response: unknown) => {
                const parsed: ApiGetAvailableActionsResponse = getAvailableActionsResponseSchema.parse(response);
                return parsed.items.map(toAvailableActionsForShipment);
            },
            providesTags: [{ type: GET_AVAILABLE_ACTIONS_API_TAG }],
        }),
        getTransportOrdersForShipment: builder.query<TransportOrder[], ShipmentIdScopedQuery>({
            query: params => ({
                url: `/transport-execution/shipments/${params.shipmentId}/transport-orders`,
                params: {
                    role: params.roles,
                    duns: params.dunsNumbers,
                },
            }),
            transformResponse: (response: unknown) => {
                const parsed: ApiTransportOrdersForShipmentsResponse =
                    transportOrdersForShipmentsResponseSchema.parse(response);
                return toTransportOrdersForShipment(parsed);
            },
            providesTags: [{ type: GET_TRANSPORT_ORDERS_API_TAG }],
        }),
        reportDeviations: builder.mutation<ReportDeviationsResponse, ReportDeviationsMutation>({
            query: (params: ReportDeviationsMutation) => {
                const requestBody: ApiReportDeviationsRequest = toApiReportDeviations(params.deviations);
                reportDeviationsRequestSchema.parse(requestBody);
                return {
                    url: `/transport-execution/shipments/${params.shipmentId}/report-deviations`,
                    method: 'POST',
                    body: requestBody,
                };
            },
            transformResponse: (response: unknown): ReportDeviationsResponse => {
                const parsed = reportDeviationsResponseSchema.parse(response);
                return toReportDeviationsResponse(parsed);
            },
            invalidatesTags: [
                { type: GET_SHIPMENTS_API_TAG },
                { type: GET_AVAILABLE_ACTIONS_API_TAG },
                { type: GET_SHIPMENTS_HISTORY_API_TAG },
                { type: GET_TRANSPORT_ORDERS_API_TAG },
            ],
        }),
        deviationDocuments: builder.mutation<void, DeviationDocumentsMutation>({
            query: (params: DeviationDocumentsMutation) => {
                deviationDocumentsSchema.parse(params.document);
                const requestBody: FormData = toApiDeviationDocuments(params.document);
                return {
                    url: `/transport-execution/shipments/${params.shipmentId}/transfer-confirmations/${params.transferConfirmationId}/deviation-documents`,
                    method: 'POST',
                    body: requestBody,
                    formData: true,
                };
            },
            invalidatesTags: [
                { type: GET_SHIPMENTS_API_TAG },
                { type: GET_AVAILABLE_ACTIONS_API_TAG },
                { type: GET_SHIPMENTS_HISTORY_API_TAG },
                { type: GET_TRANSPORT_ORDERS_API_TAG },
            ],
        }),
    }),
});

export const {
    useGetMeQuery,
    useGetShipmentsQuery,
    useGetShipmentHistoryQuery,
    useDownloadShipmentHistoryDocumentMutation,
    useLazyGetPackagingsQuery,
    useSearchPackagingMasterDataQuery,
    useConfirmShipmentTransferMutation,
    useLazyGetAvailableActionsQuery,
    useReportDeviationsMutation,
    useDeviationDocumentsMutation,
    useGetTransportOrdersForShipmentQuery,
} = transportExecutionApi;
